import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { FiUserPlus, FiPackage, FiHeart } from "react-icons/fi";

import MainContainer from "../../../components/utils/MainContainer";
import Seo from "../../../components/utils/Seo";
import ContactSectionComponent from "../../../components/utils/ContactSectionComponent";
import productsService from "../../../api/services/products";
import useReveal from "../../../hooks/useReveal";
import {
  Container,
  PageHeader,
  PageTitle,
  Section,
  SectionText,
  revealSafe,
  fadeUp,
} from "./AboutPage.styles";

/* Koraci "Kako funkcioniše" (v2 s.11.2) */
const HOW_IT_WORKS = [
  {
    icon: FiUserPlus,
    title: "Bend se pridruži",
    text: "Bend donese dizajn i dogovorimo kolekciju, bez troškova za bend.",
  },
  {
    icon: FiPackage,
    title: "Mi štampamo, prodajemo i šaljemo",
    text: "Proizvodnja, naplata i slanje PostExpresom su naša briga.",
  },
  {
    icon: FiHeart,
    title: "Bend dobija deo od svake kupovine",
    text: "Svaka kupovina se direktno deli sa bendom, kupovinom podržavaš izvođače koje slušaš.",
  },
];

/* Broj koji se "izbroji" od 0 do vrednosti, formatiran po sr-RS lokalu */
const CountUp = ({ value, duration = 900 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(value);
      return undefined;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{Number(display).toLocaleString("sr-RS")}</>;
};

export default function AboutPage() {
  // Stvarne, dinamičke brojke (v2 s.11.1), izmišljene statistike su uklonjene
  const [stats, setStats] = useState(null);

  // Scroll-reveal po sekcijama
  const [storyRef, storyVisible] = useReveal();
  const [howRef, howVisible] = useReveal();
  const [contactRef, contactVisible] = useReveal();

  useEffect(() => {
    let active = true;
    productsService
      .getProductCounts({})
      .then((response) => {
        if (!active) return;
        const data = response?.data || {};
        const bands = Array.isArray(data.bandCounts)
          ? data.bandCounts.filter((band) => (band.productCount || 0) > 0)
              .length || data.bandCounts.length
          : 0;
        setStats({
          bands,
          products: Number(data.totalCount) || 0,
        });
      })
      .catch(() => {
        if (active) setStats(null);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <MainContainer>
      <Seo title="O nama" canonical="/o-nama" />
      <Container>
        <PageHeader>
          <Label>O nama</Label>
          <PageTitle>O nama</PageTitle>
        </PageHeader>

        {/* 01 / Priča */}
        <Section ref={storyRef} $visible={storyVisible}>
          <Label>01 / Priča</Label>
          <SectionH2>Merch koji vraća sceni</SectionH2>
          <SectionText>
            МРЧ je nastao iz ljubavi prema domaćoj muzičkoj sceni i jednostavne
            ideje: da fanovi mogu da kupe zvaničan merch svojih bendova, a da
            bendovi od toga stvarno nešto imaju. Bez preprodavaca, bez
            neovlašćenih kopija, svaki proizvod na sajtu je licenciran i rađen
            u dogovoru sa bendom.
          </SectionText>
          <SectionText>
            Iza sajta stoji mala ekipa iz Srbije koja i sama ide na iste
            koncerte. Sarađujemo direktno sa bendovima i festivalima: oni
            donesu dizajn i ime, mi preuzmemo štampu, prodaju i slanje.
          </SectionText>

          {/* Stvarne brojke, jedna linija, ne kutije */}
          {stats && (stats.bands > 0 || stats.products > 0) && (
            <StatStrip>
              {stats.bands > 0 && (
                <Stat>
                  <strong>
                    <CountUp value={stats.bands} />
                  </strong>
                  <span>{stats.bands === 1 ? "bend" : "bendova"}</span>
                </Stat>
              )}
              {stats.products > 0 && (
                <Stat>
                  <strong>
                    <CountUp value={stats.products} />
                  </strong>
                  <span>proizvoda</span>
                </Stat>
              )}
              <Stat>
                <strong>2022.</strong>
                <span>na sceni od</span>
              </Stat>
            </StatStrip>
          )}
        </Section>

        {/* 02 / Kako funkcioniše */}
        <Section ref={howRef} $visible={howVisible}>
          <Label>02 / Kako funkcioniše</Label>
          <SectionH2>Tri koraka</SectionH2>
          <StepsGrid $visible={howVisible}>
            {HOW_IT_WORKS.map((step, index) => {
              const Icon = step.icon;
              return (
                <Step key={step.title}>
                  <StepIcon>
                    <Icon size={20} />
                  </StepIcon>
                  <StepNumber>{index + 1}</StepNumber>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </Step>
              );
            })}
          </StepsGrid>
          <Transparency>
            Deo prihoda od svake kupovine ide direktno bendu, dogovor sa
            bendom je javan prema bendu, bez sitnih slova.
          </Transparency>
        </Section>

        {/* Podaci o prodavcu, zakonska obaveza (v2 s.11.3).
            Zakomentarisano dok se ne popune stvarni pravni podaci
            (SELLER_INFO u utils/constants.js).

        <Section>
          <Label>03 / Podaci o prodavcu</Label>
          <SectionH2>Ko stoji iza sajta</SectionH2>
          <SellerGrid>
            <SellerItem>
              <FiFileText size={15} />
              <div>
                <dt>Pravni naziv</dt>
                <dd>{SELLER_INFO.legalName}</dd>
              </div>
            </SellerItem>
            <SellerItem>
              <FiMapPin size={15} />
              <div>
                <dt>Sedište</dt>
                <dd>{SELLER_INFO.address}</dd>
              </div>
            </SellerItem>
            <SellerItem>
              <FiFileText size={15} />
              <div>
                <dt>PIB</dt>
                <dd>{SELLER_INFO.pib}</dd>
              </div>
            </SellerItem>
            <SellerItem>
              <FiFileText size={15} />
              <div>
                <dt>Matični broj</dt>
                <dd>{SELLER_INFO.maticniBroj}</dd>
              </div>
            </SellerItem>
            <SellerItem>
              <FiPhone size={15} />
              <div>
                <dt>Telefon</dt>
                <dd>{SELLER_INFO.phone}</dd>
              </div>
            </SellerItem>
            <SellerItem>
              <FiClock size={15} />
              <div>
                <dt>Podrška</dt>
                <dd>{SELLER_INFO.supportHours}</dd>
              </div>
            </SellerItem>
          </SellerGrid>
        </Section>
        */}

        {/* 03 / Kontakt */}
        <RevealWrap ref={contactRef} $visible={contactVisible}>
          <ContactSectionComponent />
        </RevealWrap>
      </Container>
    </MainContainer>
  );
}

/* Lokalni stilovi, v2 s.11 */

const Label = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
`;

const SectionH2 = styled.h2`
  margin: 0 0 1.25rem;
  font-family: var(--font-display);
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

/* Stat traka, kao na listi bendova (v2 s.4.1) */
const StatStrip = styled.div`
  display: flex;
  align-items: baseline;
  gap: 2rem;
  flex-wrap: wrap;
  padding: 1rem 0 0;
  border-top: 1px solid var(--border);
  margin-top: 0.5rem;
`;

const Stat = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;

  strong {
    font-family: var(--font-display);
    font-size: 1.5rem;
    color: var(--brand);
    line-height: 1;
  }

  span {
    color: var(--text-secondary);
    font-size: 0.85rem;
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  /* koraci ulete jedan za drugim kad sekcija postane vidljiva */
  ${(p) =>
    p.$visible &&
    css`
      & > * {
        animation: ${fadeUp} 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
      }

      ${Array.from(
        { length: 3 },
        (_, i) => css`
          & > *:nth-child(${i + 1}) {
            animation-delay: ${200 + i * 120}ms;
          }
        `,
      )}
    `}

  @media (prefers-reduced-motion: reduce) {
    & > * {
      animation: none;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

/* Wrapper za scroll-reveal deljene kontakt sekcije */
const RevealWrap = styled.div`
  ${revealSafe}
`;

const Step = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.5rem;

  h3 {
    color: var(--text-primary);
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0.9rem 0 0.4rem;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.9rem;
    line-height: 1.55;
    margin: 0;
  }
`;

const StepIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: var(--radius-control);
  background: var(--brand-dim);
  color: var(--brand);
`;

const StepNumber = styled.span`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  font-family: var(--font-display);
  font-size: 1.75rem;
  color: var(--text-muted);
  opacity: 0.4;
  line-height: 1;
`;

/* Rečenica transparentnosti, najjači trust signal */
const Transparency = styled.p`
  margin: 1.5rem 0 0;
  padding: 1rem 1.25rem;
  background: var(--brand-dim);
  border-left: 3px solid var(--brand);
  border-radius: 0 var(--radius-control) var(--radius-control) 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.6;
`;

/* Stilovi za zakomentarisanu sekciju "Podaci o prodavcu", vratiti zajedno
   sa sekcijom kad pravni podaci budu popunjeni.

const SellerGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 0;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SellerItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);

  svg {
    color: var(--brand);
    flex-shrink: 0;
    margin-top: 0.15rem;
  }

  dt {
    color: var(--text-muted);
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 0.2rem;
  }

  dd {
    margin: 0;
    color: var(--text-primary);
    font-size: 0.92rem;
  }
`;
*/
